import React, { useState } from 'react';
import { X, CheckSquare, Square, AlertCircle, Upload, Send } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { formatMediaDateRange } from '../../lib/media';
import DateInput from '../ui/DateInput';

// Combined "Mark shoot complete" + "Update delivery" form for the shooter
// dashboard. Two modes:
//   complete: event is still 'booked' → sets actual_end_at, flips to 'editing'
//   delivery: event is 'editing' or 'delivered' → updates Drive URL +
//             three confirmation checkboxes; auto-promotes to 'delivered'
//             when all conditions are met
// The form auto-detects which mode based on event.status — same dialog,
// different fields visible.

export default function CompleteAndDeliverForm({ event, onClose, onSaved }) {
  const mode = event.status === 'booked' ? 'complete' : 'delivery';
  return mode === 'complete'
    ? <CompleteShoot event={event} onClose={onClose} onSaved={onSaved} />
    : <Delivery event={event} onClose={onClose} onSaved={onSaved} />;
}

function CompleteShoot({ event, onClose, onSaved }) {
  const [actualEndAt, setActualEndAt] = useState(toLocalInput(event.scheduledEndAt));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const r = await api.post(`/api/media/me/events/${event.id}/complete`, { actualEndAt });
      onSaved?.(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to mark complete');
      setSubmitting(false);
    }
  };

  return (
    <Shell title="Mark shoot complete" subtitle={event.eventTitle} onClose={onClose}>
      <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
        {error && <ErrBanner text={error} />}
        <div className="text-[12px] text-gray-400">
          Scheduled: {formatMediaDateRange(event.scheduledStartAt, event.scheduledEndAt)}
        </div>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Actual end time</span>
          <DateInput withTime required value={actualEndAt} onChange={setActualEndAt}
            className={inputCls} />
          <span className="block text-[11px] text-gray-500 mt-1">
            If you ran more than 30 min over, Ops will see this in the overage review queue.
          </span>
        </label>
        <Footer onCancel={onClose} submitLabel="Mark complete" submitting={submitting} icon={Send} />
      </form>
    </Shell>
  );
}

function Delivery({ event, onClose, onSaved }) {
  const [driveFolderUrl, setDriveFolderUrl] = useState(event.driveFolderUrl || '');
  const [reel, setReel] = useState(!!event.deliveredHighlightReel);
  const [photos, setPhotos] = useState(!!event.deliveredPhotoGallery);
  const [raw, setRaw] = useState(!!event.deliveredRawVideos);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const allReady = !!driveFolderUrl.trim() && reel && photos && raw;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const r = await api.put(`/api/media/me/events/${event.id}/delivery`, {
        driveFolderUrl: driveFolderUrl.trim() || null,
        deliveredHighlightReel: reel,
        deliveredPhotoGallery: photos,
        deliveredRawVideos: raw,
      });
      onSaved?.(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save');
      setSubmitting(false);
    }
  };

  return (
    <Shell title="Deliverables" subtitle={event.eventTitle} onClose={onClose}>
      <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
        {error && <ErrBanner text={error} />}
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Drive folder URL</span>
          <input type="url" value={driveFolderUrl} onChange={(e) => setDriveFolderUrl(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/…"
            className={inputCls} />
          <span className="block text-[11px] text-gray-500 mt-1">
            Parent folder with sub-folders for highlight reel, photo gallery, and raw videos (spec §11).
          </span>
        </label>

        <div className="space-y-1.5">
          <span className="block text-[11px] uppercase tracking-wider text-gray-500">Confirm each piece is uploaded</span>
          <CheckboxRow label="Edited highlight reel" checked={reel} onChange={setReel} />
          <CheckboxRow label="Curated photo gallery" checked={photos} onChange={setPhotos} />
          <CheckboxRow label="Raw videos" checked={raw} onChange={setRaw} />
        </div>

        <div className={`rounded-lg border px-3 py-2.5 text-[12px] ${
          allReady ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.06] text-[#B0FC38]'
                   : 'border-white/[0.06] bg-white/[0.02] text-gray-400'}`}>
          {allReady
            ? 'All three confirmed. Saving will mark this as Delivered and the school will see the link.'
            : 'Status stays as Editing until URL is set AND all three are confirmed. School cannot see the link until then.'}
        </div>

        <Footer onCancel={onClose} submitLabel="Save" submitting={submitting} icon={Upload} />
      </form>
    </Shell>
  );
}

function CheckboxRow({ label, checked, onChange }) {
  const Icon = checked ? CheckSquare : Square;
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-[13px] ${
        checked ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.04] text-white'
                : 'border-white/[0.06] bg-white/[0.02] text-gray-400 hover:text-white hover:border-white/[0.1]'}`}>
      <Icon className={`w-4 h-4 ${checked ? 'text-[#B0FC38]' : 'text-gray-500'}`} />
      {label}
    </button>
  );
}

function Shell({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-base font-semibold text-white tracking-tight">{title}</h2>
            <p className="text-[12px] text-gray-500 mt-0.5 truncate" title={subtitle}>{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Footer({ onCancel, submitLabel, submitting, icon: Icon }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
      <button type="button" onClick={onCancel} className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">Cancel</button>
      <button type="submit" disabled={submitting}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50">
        <Icon className="w-3.5 h-3.5" />
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  );
}

function ErrBanner({ text }) {
  return (
    <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300 flex items-start gap-2">
      <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      {text}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px] focus:outline-none focus:border-[#B0FC38]/40';

// Convert ISO → "YYYY-MM-DDTHH:MM" for <input type="datetime-local">.
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
